import { Outlet, NavLink, useLocation } from "react-router-dom";
import { AdminLogoutButton } from "./AdminRoute";
import { Logo } from "@/components/brand/Logo";
import { BrandBadge } from "@/components/brand/BrandBadge";
import {
  LayoutDashboard,
  Clock,
  Store,
  Users,
  Activity,
  ChevronLeft,
  Menu,
} from "lucide-react";
import { useState, useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  exact: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  {
    to: "/admin/pending",
    label: "Pending Resolutions",
    icon: Clock,
    exact: false,
  },
  {
    to: "/admin/markets",
    label: "Market Management",
    icon: Store,
    exact: false,
  },
  { to: "/admin/users", label: "User Management", icon: Users, exact: false },
  { to: "/admin/health", label: "System Health", icon: Activity, exact: false },
];

/**
 * AdminLayout - Sidebar navigation layout for admin dashboard.
 *
 * Features:
 * - Collapsible sidebar with icon + label navigation
 * - Active state highlighting via NavLink
 * - Admin badge in header
 * - Responsive: sidebar collapses to icon-only
 * - Outlet renders the matched child route
 */
export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  // Determine the page title from the current path
  const currentPageTitle = useMemo(() => {
    const match = NAV_ITEMS.find((item) =>
      item.exact
        ? location.pathname === item.to
        : location.pathname.startsWith(item.to) && item.to !== "/admin",
    );
    return match?.label || "Dashboard";
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={cn(
          "flex flex-col border-r border-gray-200 bg-white transition-all duration-300",
          sidebarOpen ? "w-64" : "w-16",
        )}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <Logo size="sm" />
              <BrandBadge variant="info" className="text-[10px]">
                Admin
              </BrandBadge>
            </div>
          )}
          <button
            onClick={toggleSidebar}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
            aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            {sidebarOpen ? (
              <ChevronLeft className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                  isActive
                    ? "bg-blue-50 text-primary-blue"
                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-800",
                  !sidebarOpen && "justify-center px-2",
                )
              }
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-2 border-t border-gray-200">
          <NavLink
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-800 transition-all"
          >
            <ChevronLeft className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Back to App</span>}
          </NavLink>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <h1 className="text-lg font-semibold text-gray-800">
            {currentPageTitle}
          </h1>
          <div className="flex items-center gap-4">
            <BrandBadge variant="success">Connected</BrandBadge>
            <AdminLogoutButton />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
